/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Header from './theme3/Header';
import HeroSection from './theme3/HeroSection';
import CategoryGrid from './theme3/CategoryGrid';
import ProductGrid from './theme3/ProductGrid';
import ProductRail from './theme3/ProductRail';
import './theme3/theme3-store.css';
import { useStoreBootstrap } from "./theme1/hooks/useStoreBootstrap";
import { useDisplayData, filterProductsByDivision } from "./theme1/hooks/useDisplayData";
import { useSmartCart } from './hooks/useSmartCart';
import { submitStoreCheckout, showStoreAlert } from "./utils/submitStoreCheckout";
import {
  getCurrentCustomer,
  getCustomerOrders,
  updateTenantOrder,
  cancelCustomerOrder,
  addStoreReview,
  getStoreReviews,
  getDeliveryByCity
} from './utils/storeApi';
import { n } from "./theme1/utils/theme1Helpers";

// Modals
import CartSidebar from "./theme3/CartSidebar";
import ProductModal from "./theme1/components/ProductModal";
import AuthModal from './components/AuthModal';
import OrdersModal from "./theme3/OrdersModal";
import EditOrderModal from "./theme3/EditOrderModal";
import OrderDetailsPage from "./theme3/OrderDetailsPage";
import ReviewModal from "./theme3/ReviewModal";
import WhatsAppButton from "./theme2/components/WhatsAppButton";
import Swal from "sweetalert2";

const TikTokIcon = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill={color} xmlns="http://www.w3.org/2000/svg">
    <path d="M33.5 4c.9 7.3 6.1 11.6 13.5 11.9v7.5c-4.1.2-7.7-1-11.1-3.1v14.7c0 7.3-6 13.3-13.3 13.3S9.3 42.3 9.3 35s6-13.3 13.3-13.3c.9 0 1.8.1 2.6.3v7.7c-.8-.3-1.6-.5-2.6-.5-3.1 0-5.6 2.5-5.6 5.6s2.5 5.6 5.6 5.6 5.6-2.5 5.6-5.6V4h7.3z" />
  </svg>
);

const themes = {
  original: { primary: '#0E3981', text: 'text-[#0E3981]', bg: 'bg-[#0E3981]', hover: 'hover:bg-[#112758]', primaryDark: '#112758', background: '#F3F8FB' },
  royalBlue: { primary: '#2563eb', text: 'text-[#2563eb]', bg: 'bg-[#2563eb]', hover: 'hover:bg-[#1d4ed8]', primaryDark: '#1d4ed8', background: '#F3F8FB' },
  forestGreen: { primary: '#059669', text: 'text-[#059669]', bg: 'bg-[#059669]', hover: 'hover:bg-[#047857]', primaryDark: '#047857', background: '#F3F8FB' },
  burntOrange: { primary: '#ea580c', text: 'text-[#ea580c]', bg: 'bg-[#ea580c]', hover: 'hover:bg-[#c2410c]', primaryDark: '#c2410c', background: '#F3F8FB' },
  deepRose: { primary: '#e11d48', text: 'text-[#e11d48]', bg: 'bg-[#e11d48]', hover: 'hover:bg-[#be123c]', primaryDark: '#be123c', background: '#F9FAFB' },
};

const Theme3Store = ({ tenantId, tenant }) => {
  const [currentTheme, setCurrentTheme] = useState('original');
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  const [showCartSidebar, setShowCartSidebar] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false); // تم الإضافة
  const [showAllReviews, setShowAllReviews] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const readSavedCustomer = useCallback(() => {
    try {
      const saved = localStorage.getItem("storeCustomer");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }, []);

  const [customer, setCustomer] = useState(() => readSavedCustomer());

  const [customerOrders, setCustomerOrders] = useState([]);
  const [storeReviews, setStoreReviews] = useState([]); // تم الإضافة
  const [newReview, setNewReview] = useState({ rating: 5, description: "" }); // تم الإضافة
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);

  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [deliveryForm, setDeliveryForm] = useState({
    name: "", email: "", phone: "", address: "", notes: "", cityId: localStorage.getItem("selectedCityId") || "", cityName: localStorage.getItem("cartSavedCityName") || ""
  });
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const asText = (value) => (value === null || value === undefined ? "" : String(value).trim());
  const asNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  const {
    loading, storeData, apiProducts, apiCategories, apiCoupons, apiBanners, apiTags, cities
  } = useStoreBootstrap(tenantId);

  const {
    displayProducts, displayCategories, displayCoupons
  } = useDisplayData({
    apiProducts, apiCategories, apiCoupons, apiTags
  });

  const {
    cart, addItem, updateQuantity, removeItem, clearCart, refreshCart, mergeGuestCartToAccount,
  } = useSmartCart(tenantId);

  const resolveThemeKey = (name = "") => {
    const key = name.trim();
    return themes[key] ? key : null;
  };

  useEffect(() => {
    const key =
      resolveThemeKey(tenant?.colorName) ||
      resolveThemeKey(storeData?.colorName);
    if (key) setCurrentTheme(key);
  }, [tenant?.colorName, storeData?.colorName]);

  useEffect(() => {
    const syncCustomer = () => setCustomer(readSavedCustomer());
    const handleVisibility = () => {
      if (!document.hidden) syncCustomer();
    };

    window.addEventListener("storage", syncCustomer);
    window.addEventListener("focus", syncCustomer);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("storage", syncCustomer);
      window.removeEventListener("focus", syncCustomer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [readSavedCustomer]);

  useEffect(() => {
    if (displayProducts?.length) {
      window.__STORE_PRODUCTS__ = displayProducts;
      refreshCart();
    }
  }, [displayProducts, refreshCart]);

  // Handlers
  const applyCouponCode = (rawCode) => {
    setCouponError("");
    if (cart.length === 0) return setCouponError("السلة فارغة");
    const code = (rawCode || couponCode).toUpperCase().trim();
    const coupon = displayCoupons.find(c => c.code === code);
    if (!coupon) return setCouponError("كوبون غير صحيح");
    setAppliedCoupon(coupon);
    setCouponCode("");
    localStorage.setItem("storeCoupon", JSON.stringify(coupon));
  };

  const handleApplyCoupon = (e) => {
    e.preventDefault();
    applyCouponCode();
  };

  const handleCategoryClick = (categoryId, subCategoryId = null) => {
    const nextCategoryId = Number(categoryId);
    if (subCategoryId) {
      setSelectedCategory(nextCategoryId);
      setSelectedSubCategory(Number(subCategoryId));
    } else {
      setSelectedCategory(prev => (Number(prev) === nextCategoryId ? 0 : nextCategoryId));
      setSelectedSubCategory(null);
    }
  };

  const loadCustomerOrders = async () => {
    const cust = getCurrentCustomer();
    if (!cust?.customerId) return;
    const res = await getCustomerOrders(tenantId, cust.customerId);
    if (res.success) setCustomerOrders(res.data);
  };

  // Fetch orders when the user opens "طلباتي" (mirrors Theme1 behavior)
  useEffect(() => {
    if (showOrdersModal) {
      loadCustomerOrders();
    }
  }, [showOrdersModal]);

  const handleOpenProduct = ({ productId, variantId }) => {
    const p = displayProducts.find(x => x.id === productId);
    if (!p) return;
    const v = p.variants.find(x => Number(x.variantId) === Number(variantId));
    setSelectedProduct({ productId, variantId: v?.variantId });
    setShowProductModal(true);
  };

  const handleAddToCart = async (data, qtyToAdd = 1) => {
    const res = await addItem(data, qtyToAdd);
    if (res?.success) { refreshCart(); setShowCartSidebar(true); }
    else { Swal.fire("تنبيه", res?.error || "فشل الإضافة", "warning"); }
  };
  // جلب التقييمات عند تحميل الصفحة
  useEffect(() => {
    const fetchReviews = async () => {
      const res = await getStoreReviews(tenantId);
      if (res.success) {
        setStoreReviews(res.data.filter(r => r.isActive === 1));
      }
    };
    fetchReviews();
  }, [tenantId]);
  const handleCheckoutSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isSubmittingRef.current || isSubmitting) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const result = await submitStoreCheckout({
        tenantId,
        cart,
        cities,
        deliveryForm,
        deliveryInfo,
        subtotal,
        total,
        couponDiscount,
      });

      if (result.duplicate) return;

      if (!result.ok) {
        await showStoreAlert({
          icon: "error",
          title: result.title || "خطأ",
          text: result.error,
        });
        return;
      }

      localStorage.removeItem("storeCoupon");
      setAppliedCoupon(null);
      setCouponError("");
      clearCart();
      await refreshCart();
      setShowCartSidebar(false);

      await showStoreAlert({
        icon: "success",
        title: "تم إنشاء الطلب بنجاح",
        text: "سيتم التواصل معك لتأكيد الطلب",
      });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleDeleteOrder = async (order) => {
    const confirm = await Swal.fire({
      title: "تأكيد الحذف",
      text: "هل تريد حذف هذا الطلب نهائياً؟",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "نعم احذف",
      cancelButtonText: "إلغاء"
    });

    if (!confirm.isConfirmed) return;

    const res = await cancelCustomerOrder(
      tenantId,
      order.orderId,
      order.statusId
    );

    if (res?.success) {
      setCustomerOrders(prev =>
        prev.filter(o => o.orderId !== order.orderId)
      );
      setSelectedOrderDetails(null);
      Swal.fire("تم", "تم حذف الطلب بنجاح", "success");
    } else {
      Swal.fire("خطأ", res?.error || "فشل حذف الطلب", "error");
    }
  };

  const saveEditedOrder = async (order) => {
    if (!order?.items || order.items.length === 0) {
      Swal.fire("خطأ", "الطلب فارغ", "error");
      return;
    }

    const city = cities.find(
      c => Number(c.cityId) === Number(order.cityId)
    );

    if (!city) {
      Swal.fire("خطأ", "المحافظة غير صحيحة", "error");
      return;
    }

    const normalizedOrder = {
      ...order,
      items: order.items.map(d => ({
        itemId: asNumber(d.itemId, 0),
        variantId: asNumber(d.variantId, 0),
        itemName: asText(d.itemName),
        variantName: asText(d.variantName),
        colorName: asText(d.colorName),
        size: asText(d.size),
        quantity: asNumber(d.quantity, 0),
        price: asNumber(d.price, 0),
        imageUrl: asText(d.imageUrl),
        stockQty: asNumber(d.stockQty || 0, 0),
        discount: asNumber(d.discount || 0, 0),
        lineTotal: asNumber(d.quantity, 0) * asNumber(d.price, 0)
      }))
    };

    const res = await updateTenantOrder(tenantId, normalizedOrder, city);

    if (res.success) {
      const savedOrder = res.data?.order || res.data?.data || res.data || normalizedOrder;
      setCustomerOrders(prev =>
        prev.map(o =>
          o.orderId === order.orderId
            ? { ...o, ...savedOrder }
            : o
        )
      );

      setEditingOrder(null);
      Swal.fire("تم", "تم حفظ التعديلات بنجاح", "success");
    } else {
      Swal.fire("خطأ", res.error || "فشل حفظ التعديلات", "error");
    }
  };

  // أضف هذا الـ Effect داخل مكون Theme3Store
  useEffect(() => {
    const savedCustomer = JSON.parse(localStorage.getItem("storeCustomer"));
    if (savedCustomer) {
      setDeliveryForm({
        name: savedCustomer.name || "",
        email: savedCustomer.email || "",
        phone: savedCustomer.phone || "",
        address: savedCustomer.address || "",
        notes: savedCustomer.notes || "",
        cityId: savedCustomer.cityId || localStorage.getItem("selectedCityId") || ""
      });
    }
  }, [customer]);

  useEffect(() => {
    if (deliveryForm.cityId && tenantId) {
      getDeliveryByCity(tenantId, deliveryForm.cityId).then(res => {
        if (res) setDeliveryInfo(res);
        else setDeliveryInfo(null);
      });
    } else {
      setDeliveryInfo(null);
    }
  }, [deliveryForm.cityId, tenantId]);
  const filteredProducts = useMemo(() => {
    let data = filterProductsByDivision(displayProducts, selectedCategory, selectedSubCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(p => p.name?.toLowerCase().includes(q));
    }
    return data;
  }, [displayProducts, selectedCategory, selectedSubCategory, searchQuery]);

  const hasProductDiscount = (p) =>
    Number(p?.discountValue) > 0 || (p?.variants || []).some((v) => Number(v.discount) > 0);

  const saleProducts = useMemo(
    () => (displayProducts || []).filter(hasProductDiscount).slice(0, 12),
    [displayProducts]
  );
  const featuredProducts = useMemo(
    () => (displayProducts || []).filter((p) => p.isBestseller).slice(0, 12),
    [displayProducts]
  );
  const newProducts = useMemo(() => {
    const tagged = (displayProducts || []).filter((p) => p.isNewProduct);
    if (tagged.length) return tagged.slice(0, 12);
    return [...(displayProducts || [])]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 12);
  }, [displayProducts]);

  const subtotal = useMemo(() => cart.reduce((s, i) => s + (i.price * i.quantity), 0), [cart]);
  const couponDiscount = useMemo(() => {
    if (!appliedCoupon) return 0;
    const v = n(appliedCoupon.discountValue);
    return appliedCoupon.discountType === 1 ? (subtotal * v) / 100 : Math.min(v, subtotal);
  }, [appliedCoupon, subtotal]);
  const shipping = useMemo(() => {
    if (!deliveryInfo) return 0;
    return Number(deliveryInfo.deliveryCompanyFees || 0);
  }, [deliveryInfo]);

  const total = subtotal - couponDiscount + shipping;

  if (loading) return <div className="min-h-screen flex items-center justify-center">جاري التحميل...</div>;
  const theme = themes[currentTheme] || themes.original;

  const socials = storeData?.social ? [
    { key: "facebook", label: "فيسبوك", icon: "fab fa-facebook-f", url: storeData.social.facebook },
    { key: "instagram", label: "إنستغرام", icon: "fab fa-instagram", url: storeData.social.instagram },
    { key: "snapchat", label: "سناب شات", icon: "fab fa-snapchat-ghost", url: storeData.social.snapchat },
    { key: "tiktok", label: "تيك توك", url: storeData.social.tiktok, renderIcon: () => <TikTokIcon size={22} /> }
  ].filter(s => s.url) : [];

  const scrollToProducts = () => {
    document.getElementById("all-products")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="t3-store" dir="rtl">
      <Header
        theme={theme}
        storeData={storeData}
        cartCount={cart.reduce((s, i) => s + i.quantity, 0)}
        onCartClick={() => setShowCartSidebar(true)}
        onUserClick={() => setShowAuthModal(true)}
        onOrdersClick={() => {
          loadCustomerOrders();
          setShowOrdersModal(true);
        }}
        searchQuery={searchQuery}
        onSearchChange={(e) => setSearchQuery(e.target.value)}
        customer={customer}
        onLogout={() => { localStorage.clear(); window.location.reload(); }}
      />

      <HeroSection
        theme={theme}
        apiBanners={apiBanners}
        storeData={storeData}
        onShop={scrollToProducts}
      />

      <ProductRail
        id="sale-section"
        title="التخفيضات"
        subtitle="أفضل العروض المتوفرة حالياً"
        products={saleProducts}
        theme={theme}
        onAddToCart={handleAddToCart}
        onOpenProduct={handleOpenProduct}
        onViewAll={scrollToProducts}
      />

      <ProductRail
        id="featured-section"
        title="المنتجات المميزة"
        subtitle="اختيارات نرشّحها لك"
        products={featuredProducts.length ? featuredProducts : displayProducts.slice(0, 12)}
        theme={theme}
        onAddToCart={handleAddToCart}
        onOpenProduct={handleOpenProduct}
        onViewAll={scrollToProducts}
      />

      <ProductRail
        id="new-section"
        title="وصلنا حديثاً"
        subtitle="أحدث الإضافات إلى المتجر"
        products={newProducts}
        theme={theme}
        onAddToCart={handleAddToCart}
        onOpenProduct={handleOpenProduct}
        onViewAll={scrollToProducts}
      />

      <CategoryGrid
        categories={displayCategories}
        selectedCategory={selectedCategory}
        selectedSubCategory={selectedSubCategory}
        onCategoryClick={handleCategoryClick}
        theme={theme}
      />

      <ProductGrid
        theme={theme}
        products={filteredProducts}
        onAddToCart={handleAddToCart}
        onOpenProduct={handleOpenProduct}
      />

      <section className="t3-container t3-trust">
        <div className="t3-trust-card">
          <span style={{ background: theme.primary }}>🚚</span>
          <div>
            <h3>توصيل آمن وسريع</h3>
            <p>نضمن وصول منتجاتك إلى باب منزلك</p>
          </div>
        </div>
        <div className="t3-trust-card">
          <span style={{ background: theme.primary }}>💬</span>
          <div>
            <h3>خدمة العملاء</h3>
            <p>دعم متواصل لاستفساراتكم</p>
          </div>
        </div>
        <div className="t3-trust-card">
          <span style={{ background: theme.primary }}>↩️</span>
          <div>
            <h3>ضمان إرجاع</h3>
            <p>سهولة في الاسترجاع حسب سياسة المتجر</p>
          </div>
        </div>
      </section>

      <section id="about-section" className="t3-about">
        <div className="t3-container">
          <div className="t3-about-box">
            <h2>عن المتجر</h2>
            <p>{storeData.description || storeData.storeSlogan || "نحن متجر إلكتروني متخصص في تقديم أفضل المنتجات والخدمات لعملائنا الكرام."}</p>
          </div>
        </div>
      </section>

      <section className="t3-reviews">
        <div className="t3-container">
          <div className="t3-section-head">
            <div>
              <h2>آراء العملاء</h2>
              <p>ماذا يقول زبائننا عن تجربتهم</p>
            </div>
            <button
              type="button"
              className="t3-link-all"
              style={{ color: theme.primary }}
              onClick={() => setShowReviewModal(true)}
            >
              أضف تقييمك
            </button>
          </div>
          {storeReviews.length > 0 ? (
            <>
              <div className="t3-review-grid">
                {(showAllReviews ? storeReviews : storeReviews.slice(0, 3)).map((r, i) => (
                  <div key={i} className="t3-review-card">
                    <strong>{r.customerName}</strong>
                    <div className="text-yellow-400 my-2">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</div>
                    <p>{r.description}</p>
                  </div>
                ))}
              </div>
              {storeReviews.length > 3 && (
                <div style={{ textAlign: "center", marginTop: 16 }}>
                  <button type="button" className="t3-link-all" style={{ color: theme.primary }} onClick={() => setShowAllReviews(!showAllReviews)}>
                    {showAllReviews ? "إخفاء" : "مشاهدة المزيد"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="t3-empty">لا توجد تقييمات بعد. كن أول من يقيمنا!</p>
          )}
        </div>
      </section>

      <footer id="contact-section" className="t3-footer">
        <div className="t3-container t3-footer-grid">
          <div>
            <h3>عن المتجر</h3>
            <p>{storeData.storeName || "المتجر الإلكتروني"}</p>
            <p>{storeData.storeSlogan || "نحن متجر إلكتروني متخصص في تقديم أفضل المنتجات والخدمات لعملائنا الكرام."}</p>
          </div>
          <div>
            <h3>روابط سريعة</h3>
            <ul>
              <li onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>الرئيسية</li>
              <li onClick={scrollToProducts}>المنتجات</li>
              <li onClick={() => document.getElementById("about-section")?.scrollIntoView({ behavior: "smooth" })}>من نحن</li>
              <li onClick={() => document.getElementById("contact-section")?.scrollIntoView({ behavior: "smooth" })}>اتصل بنا</li>
            </ul>
          </div>
          <div>
            <h3>المعلومات</h3>
            <ul>
              {storeData.policies?.privacy && (
                <li onClick={() => Swal.fire({ title: "سياسة الخصوصية", text: storeData.policies.privacy, confirmButtonText: "حسناً", confirmButtonColor: theme.primary })}>سياسة الخصوصية</li>
              )}
              {storeData.policies?.delivery && (
                <li onClick={() => Swal.fire({ title: "الشحن والتوصيل", text: storeData.policies.delivery, confirmButtonText: "حسناً", confirmButtonColor: theme.primary })}>الشحن والتوصيل</li>
              )}
              {storeData.policies?.refund && (
                <li onClick={() => Swal.fire({ title: "سياسة الإرجاع", text: storeData.policies.refund, confirmButtonText: "حسناً", confirmButtonColor: theme.primary })}>سياسة الإرجاع</li>
              )}
              {!storeData.policies?.privacy && !storeData.policies?.delivery && !storeData.policies?.refund && (
                <>
                  <li>سياسة الخصوصية</li>
                  <li>الشروط والأحكام</li>
                </>
              )}
            </ul>
          </div>
          <div>
            <h3>تواصل معنا</h3>
            {storeData.phone && <p>{storeData.phone}</p>}
            {storeData.email && <p>{storeData.email}</p>}
            {socials.length > 0 ? (
              <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
                {socials.map((s) => (
                  <a key={s.key} href={s.url} target="_blank" rel="noreferrer" title={s.label}>
                    {s.renderIcon ? s.renderIcon() : <i className={`${s.icon} text-xl`} />}
                  </a>
                ))}
              </div>
            ) : (
              <p>جميع الحقوق محفوظة</p>
            )}
          </div>
        </div>
        <div className="t3-container t3-copy">جميع الحقوق محفوظة {storeData.storeName || ""} © {new Date().getFullYear()}</div>
      </footer>

      {/* Modals */}
      {showCartSidebar && (
        <CartSidebar
          tenantId={tenantId}
          theme={theme}
          cities={cities}
          isMobile={typeof window !== 'undefined' && window.innerWidth < 768}
          cart={cart}
          appliedCoupon={appliedCoupon}
          couponDiscount={couponDiscount}
          subtotal={subtotal}
          shipping={shipping}
          total={total}
          displayCoupons={displayCoupons}
          couponCode={couponCode}
          setCouponCode={setCouponCode}
          couponError={couponError}
          applyCouponCode={applyCouponCode}
          handleApplyCoupon={handleApplyCoupon}
          handleUpdateQty={updateQuantity}
          handleRemove={removeItem}
          deliveryForm={deliveryForm}
          setDeliveryForm={setDeliveryForm}
          handleCheckoutSubmit={handleCheckoutSubmit}
          isSubmitting={isSubmitting}
          setShowAuthModal={setShowAuthModal}
          onClose={() => setShowCartSidebar(false)}
        />
      )}

      {showProductModal && selectedProduct && (
        <ProductModal
          showProductModal={showProductModal}
          setShowProductModal={setShowProductModal}
          selectedProduct={selectedProduct}
          products={displayProducts}
          handleAddToCart={handleAddToCart}
          theme={theme}
          isMobile={typeof window !== 'undefined' && window.innerWidth < 768}
        />
      )}

      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          tenantId={tenantId}
          theme={{ primary: theme.primary, secondary: theme.primaryDark }}
          onSuccess={(data) => {
            setCustomer(data);
            loadCustomerOrders();
            setShowAuthModal(false);
            mergeGuestCartToAccount();
          }}
        />
      )}

      {showOrdersModal && !selectedOrderDetails && (
        <OrdersModal
          show={showOrdersModal}
          onClose={() => setShowOrdersModal(false)}
          orders={customerOrders}
          openOrderDetails={(order) => {
            setSelectedOrderDetails(order);
            setShowOrdersModal(false);
          }}
          theme={theme}
        />
      )}

      {selectedOrderDetails &&
        <OrderDetailsPage
          order={selectedOrderDetails}
          theme={theme}
          onClose={() => setSelectedOrderDetails(null)}
          onEdit={(order) => {
            setEditingOrder(JSON.parse(JSON.stringify(order)));
            setSelectedOrderDetails(null);
          }}
          onDelete={handleDeleteOrder}
        />
      }

      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          products={apiProducts}
          theme={theme}
          onCancel={() => setEditingOrder(null)}
          onSave={saveEditedOrder}
        />
      )}

      {/* Review Modal الربط الصحيح */}
      {showReviewModal && (
        <ReviewModal
          show={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          tenantId={tenantId}
          newReview={newReview}
          setNewReview={setNewReview}
          addStoreReview={addStoreReview}
          getStoreReviews={getStoreReviews}
          setStoreReviews={setStoreReviews}
          theme={theme}
        />
      )}

      <WhatsAppButton storeData={storeData} />

      <style>{`
        html { scroll-behavior: smooth; }
        .scroll-mt-20 { scroll-margin-top: 80px; }
      `}</style>
    </div>
  );
};

export default Theme3Store;
